import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { submissionAPI } from '@/utils/api';
import { formatDate, formatMonth } from '@/utils/formatters';
import { FileCheck, Check, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

const AdminSubmissions = () => {
  const { user } = useOutletContext();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadSubmissions();
  }, []);
  
  const loadSubmissions = async () => {
    try {
      const response = await submissionAPI.list();
      setSubmissions(response.data);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprove = async (submissionId) => {
    try {
      await submissionAPI.approve(submissionId);
      toast.success('Submission approved!');
      loadSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };
  
  const handleReopen = async (submissionId) => {
    try {
      await submissionAPI.reopen(submissionId);
      toast.success('Submission reopened!');
      loadSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen');
    }
  };
  
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning',
      approved: 'bg-success/10 text-success',
      reopened: 'bg-creative/10 text-creative',
    };
    return colors[status] || 'bg-secondary text-secondary-foreground';
  };
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold text-primary mb-2">Review Submissions</h1>
          <p className="text-muted-foreground">Approve or reopen monthly work submissions from translators</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="w-5 h-5 text-accent" />
              All Submissions ({submissions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12">
                <FileCheck className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No submissions found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((submission) => (
                  <Card 
                    key={submission.submission_id}
                    className="hover:shadow-md transition-shadow"
                    data-testid={`submission-${submission.submission_id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div>
                              <h3 className="text-lg font-semibold text-primary">{submission.translator_name || 'Unknown Translator'}</h3>
                              <p className="text-sm text-muted-foreground">Month: {formatMonth(submission.month)}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 text-sm">
                            <Badge className={getStatusColor(submission.status)}>
                              {submission.status}
                            </Badge>
                            <span className="text-muted-foreground">
                              Submitted: {formatDate(submission.submitted_at)}
                            </span>
                            {submission.approved_at && (
                              <span className="text-success">
                                Approved: {formatDate(submission.approved_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          {submission.status === 'pending' && (
                            <Button
                              onClick={() => handleApprove(submission.submission_id)}
                              size="sm"
                              className="bg-success hover:bg-success/90"
                              data-testid={`btn-approve-${submission.submission_id}`}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                          )}
                          {submission.status === 'approved' && (
                            <Button
                              onClick={() => handleReopen(submission.submission_id)}
                              size="sm"
                              variant="outline"
                              data-testid={`btn-reopen-${submission.submission_id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-2" />
                              Reopen
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminSubmissions;
